<template>
    <div class="form-container card shadow-sm">
      <div class="form-header d-flex justify-content-between align-items-center">
        <h4 class="form-title">{{ valor.id ? "Editar Valor" : "Novo Valor" }}</h4>
        <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
      </div>
      <BaseForm
        :submitText="valor.id ? 'Salvar' : 'Salvar'"
        :cancelText="'Cancelar'"
        :isFormValid="isFormValid"
        :isSubmitting="isSubmitting"
        @submit="salvarValor"
        @cancel="voltar"
      >
        <!-- Dados do Valor -->
        <FormSection title="Informações do Valor">
          <div class="d-flex justify-content-between">
            <!-- Tipo -->
            <div class="form-group flex-grow-1 me-3">
              <label for="tipo">Tipo <span class="required">*</span></label>
              <select
                id="tipo"
                v-model="valor.tipo_id"
                class="form-select"
                :class="{ 'error-input': errors.tipo_id }"
              >
                <option disabled value="">Selecione um tipo</option>
                <option v-for="tipo in tipos" :key="tipo.value" :value="tipo.value">
                  {{ tipo.text }}
                </option>
              </select>
              <span v-if="errors.tipo_id" class="error-message">{{ errors.tipo_id }}</span>
            </div>
  
            <!-- Valor -->
            <div class="form-group flex-grow-1 me-3">
              <label for="valor">Valor (R$) <span class="required">*</span></label>
              <input
                id="valor"
                type="number"
                step="0.01"
                v-model="valor.valor"
                class="form-control"
                :class="{ 'error-input': errors.valor }"
                placeholder="Digite o valor"
              />
              <span v-if="errors.valor" class="error-message">{{ errors.valor }}</span>
            </div>
  
            <!-- Status -->
            <div class="form-group">
              <label for="status">Status <span class="required">*</span></label>
              <div class="form-switch">
                <input
                  type="checkbox"
                  id="status"
                  v-model="valor.ativo"
                  class="form-check-input"
                />
                <label for="status" class="form-check-label">
                  {{ valor.ativo ? "Ativo" : "Inativo" }}
                </label>
              </div>
            </div>
          </div>
        </FormSection>
  
        <FormSection title="Data do Valor">
          <div class="form-group mt-3">
            <label for="data">Data <span class="required">*</span></label>
            <input
              id="data"
              type="date"
              v-model="valor.data"
              class="form-control"
              :class="{ 'error-input': errors.data }"
            />
            <span v-if="errors.data" class="error-message">{{ errors.data }}</span>
          </div>
        </FormSection>
      </BaseForm>
    </div>
  </template>
  
  <script>
  import { reactive, ref, computed, onMounted } from "vue";
  import { useRouter, useRoute } from "vue-router";
  import axios from "@/services/axios";
  import BaseForm from "@/components/Layout/BaseForm.vue";
  import FormSection from "@/components/Layout/FormSection.vue";
  
  export default {
    components: { BaseForm, FormSection },
    setup() {
      const router = useRouter();
      const route = useRoute();
  
      const valor = reactive({
        id: null,
        tipo_id: "",
        valor: null,
        data: "",
        ativo: true,
      });
  
      const tipos = ref([]);
      const errors = reactive({});
      const isSubmitting = ref(false);
  
      // Formatar data para o formato yyyy-MM-dd
      const formatarDataIsoParaInput = (data) => {
        if (!data) return ""; // Retorna vazio se a data não for válida
        const date = new Date(data);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };
  
      // Carregar tipos de valores
      const carregarTipos = async () => {
            try {
                const response = await axios.get(`/financeiro/tipos-valores`);
                tipos.value = response.data.map((tipo) => ({
                value: tipo.id,
                text: tipo.nome,
                }));

                // Garantir que o tipo_id seja reavaliado após carregar os tipos
                if (valor.tipo_id) {
                const tipoEncontrado = tipos.value.find((tipo) => tipo.value === valor.tipo_id);
                if (!tipoEncontrado) {
                    console.warn(`Tipo ID ${valor.tipo_id} não corresponde a nenhum tipo carregado.`);
                }
                }
            } catch (error) {
                console.error("Erro ao carregar tipos:", error);
            }
        };
  
      // Carregar detalhes do valor
      const carregarValor = async () => {
            const id = route.params.id;
            if (!id) return;

            try {
                const response = await axios.get(`/financeiro/valores/${id}`);
                const data = response.data;

                valor.id = data.id;
                valor.valor = data.valor;
                valor.data = formatarDataIsoParaInput(data.data); // Formatar a data
                valor.ativo = data.ativo;

                // Mapear `tipo` para `tipo_id` usando a lista de tipos carregados
                const tipoEncontrado = tipos.value.find((tipo) => tipo.text === data.tipo);
                if (tipoEncontrado) {
                valor.tipo_id = tipoEncontrado.value; // Atribuir o ID correspondente
                } else {
                valor.tipo_id = null; // Caso não encontre, deixar nulo
                console.warn(`Tipo "${data.tipo}" não encontrado na lista de tipos carregados.`);
                }
            } catch (error) {
                console.error("Erro ao carregar valor:", error);
            }
        };
  
      const validateForm = () => {
        errors.tipo_id = valor.tipo_id ? "" : "O tipo é obrigatório.";
        errors.valor = valor.valor ? "" : "O valor é obrigatório.";
        errors.data = valor.data ? "" : "A data é obrigatória.";
      };
  
      const isFormValid = computed(() => {
        return Boolean(
          !Object.values(errors).some((error) => error) &&
            valor.tipo_id &&
            valor.valor &&
            valor.data
        );
      });
  
      const salvarValor = async () => {
        if (isSubmitting.value) return;
  
        validateForm();
  
        if (!isFormValid.value) {
          console.error("O formulário contém erros:", errors);
          return;
        }
  
        isSubmitting.value = true;
        try {
          const method = valor.id ? "put" : "post";
          const url = valor.id
            ? `/financeiro/valores/${valor.id}`
            : "/financeiro/valores";
  
          await axios[method](url, valor);
  
          router.push("/financeiro/valores");
        } catch (error) {
          console.error("Erro ao salvar valor:", error);
        } finally {
          isSubmitting.value = false;
        }
      };
  
      const voltar = () => {
        router.push("/financeiro/valores");
      };
  
      onMounted(async () => {
        await carregarTipos(); // Carregar tipos primeiro
        carregarValor(); // Só então carregar os detalhes do valor
    });
  
      return {
        valor,
        tipos,
        errors,
        isFormValid,
        isSubmitting,
        salvarValor,
        voltar,
      };
    },
  };
</script>
    
  <style scoped>
  .form-container {
    max-width: 90%;
    margin: 0 auto;
    padding: 20px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #fff;
  }
  
  .form-title {
    margin-bottom: 10px;
    font-size: 1.5rem;
    color: #333;
  }
  
  .form-group {
    margin-bottom: 15px;
  }
  
  .required {
    color: red;
  }
  
  .form-switch {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  </style>
  